import { Component, Input, OnInit } from "@angular/core";
import { TranslationService } from "@services/translation.service";
import { UnitConversionService, UnitConversionType } from "@services/unitConversion.service";
import { VeriDate } from "@services/veriDate.service";
import './pva-bar-chart.component.scss';

enum IPvaBarChartType {
    Planned,
    Actual
}

interface IPvaBarChartObject {
    type: IPvaBarChartType;
    value: number;
    percentageHeight: number;
    tooltipLabel: string;
}

@Component({
    selector: 'pva-bar-chart',
    template: require('./pva-bar-chart.component.html')
})
export class PvaBarChartComponent implements OnInit {
    @Input() plannedFigure: number;
    @Input() actualFigure: number;
    @Input() distance: boolean;
    @Input() duration: boolean;

    bars: IPvaBarChartObject[];
    differenceLabel: string;
    differenceLabelClass: string;
    distanceUnit: string;

    chartLabel: string;
    show = false;

    constructor(
        private readonly translationService: TranslationService,
        private readonly unitConversionService: UnitConversionService,
        private readonly veriDate: VeriDate) {

    }

    ngOnInit(): void {
        if (!this.isValidInput()) {
            console.error("Input missing or invalid");
            this.show = false;
            return;
        } else {
            this.show = true;
        }
        if (this.distance) {
            this.distanceUnit = this.unitConversionService.getConversionLabel(UnitConversionType.Distance);
        }
        this.createBars();
        this.setChartLabels();
        this.defineHeights();
    }

    private isValidInput(): boolean {
        if (isNaN(this.plannedFigure) || isNaN(this.actualFigure) || !this.plannedFigure || !this.actualFigure) {
            return false;
        }
        else {
            return true;
        }
    }

    private createBars() {
        this.bars = [];
        this.bars = [
            {
                type: IPvaBarChartType.Planned,
                value: this.plannedFigure,
                percentageHeight: 100,
                tooltipLabel: this.getTooltipLabel(this.plannedFigure)
            },
            {
                type: IPvaBarChartType.Actual,
                value: this.actualFigure,
                percentageHeight: 100,
                tooltipLabel: this.getTooltipLabel(this.actualFigure)
            }
        ];
        if (this.distance) {
            this.bars.forEach(bar => {
                bar.value = this.getConvertedDistance(bar.value);
            })
        }
    }

    private setChartLabels() {
        let difference = this.actualFigure - this.plannedFigure;
        if (isNaN(difference)) {
            return;
        }
        const prefix = difference > 0 ? '+' : '';
        if (this.distance) {
            difference = this.unitConversionService.convert(difference, UnitConversionType.Distance, 1);
            this.differenceLabel = `${prefix}${difference} ${this.distanceUnit}`;
            this.chartLabel = this.translationService.t('Tms.Distance');
        }
        if (this.duration) {
            this.differenceLabel = `${prefix}${this.veriDate.minutesToDurationString(difference)}`;
            this.chartLabel = this.translationService.t('Tms.Duration');
        }
        if (this.actualFigure < this.plannedFigure) {
            this.differenceLabelClass = 'color-ontime';
        }
        else if (this.actualFigure > this.plannedFigure) {
            this.differenceLabelClass = 'color-missed';
        }
    }

    private getConvertedDistance(value) {
        let valueToConvert = value.toString();
        return this.unitConversionService.convert(valueToConvert, UnitConversionType.Distance, 1);
    }

    private getTooltipLabel(value: number): string {
        if (!value || isNaN(value)) {
            return;
        }
        if (this.distance) {
            let distance = this.getConvertedDistance(value);
            return `${distance} ${this.distanceUnit}`;
        }
        if (this.duration) {
            return this.veriDate.minutesToDurationString(value);
        }
    }

    private defineHeights() {
        if (this.bars[0].value > this.bars[1].value) {
            this.bars[0].percentageHeight = 100;
            this.bars[1].percentageHeight = this.bars[1].value / this.bars[0].value * 100;
        }
        else if (this.bars[1].value > this.bars[0].value) {
            this.bars[1].percentageHeight = 100;
            this.bars[0].percentageHeight = this.bars[0].value / this.bars[1].value * 100;
        }
        else if (this.bars[1].value === this.bars[0].value) {
            this.bars[1].percentageHeight = 100;
            this.bars[0].percentageHeight = 100;
        }
    }

}
